/**
 * 전화번호 숫자패드 모달
 * - 안드로이드 키보드 대신 자체 숫자패드로 전화번호 입력
 * - 010 은 prefix 로 고정 표시, 뒤 8자리만 입력
 * - 확인 시 11자리 전화번호(또는 빈 문자열)를 onConfirm 으로 전달
 */

import React, { useState, useEffect } from 'react';
import './PhoneKeypadModal.css';

const NUMPAD_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['clear', '0', 'backspace'],
];

const extractDigits = (fullPhone) => {
  const onlyDigits = (fullPhone || '').replace(/\D/g, '');
  if (onlyDigits.startsWith('010')) return onlyDigits.slice(3, 11);
  return onlyDigits.slice(0, 8);
};

const PhoneKeypadModal = ({
  open,
  title = '전화번호 입력',
  initialPhone = '',
  allowEmpty = false,
  onClose,
  onConfirm,
}) => {
  const [digits, setDigits] = useState('');

  useEffect(() => {
    if (open) setDigits(extractDigits(initialPhone));
  }, [open, initialPhone]);

  if (!open) return null;

  const handleKeyPress = (key) => {
    if (key === 'backspace') {
      setDigits((prev) => prev.slice(0, -1));
    } else if (key === 'clear') {
      setDigits('');
    } else {
      setDigits((prev) => (prev.length < 8 ? prev + key : prev));
    }
  };

  const handleConfirm = () => {
    if (digits.length === 0 && allowEmpty) {
      onConfirm('');
      onClose();
      return;
    }
    if (digits.length !== 8) return;
    onConfirm('010' + digits);
    onClose();
  };

  const canConfirm = digits.length === 8 || (allowEmpty && digits.length === 0);

  return (
    <div className="pkm-overlay" onClick={onClose}>
      <div className="pkm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pkm-title">{title}</div>

        <div className="pkm-display">
          <span className="pkm-prefix">010</span>
          <span className={`pkm-digits ${digits.length === 0 ? 'is-empty' : ''}`}>
            {digits.length === 0 ? '뒤 8자리 입력' : (
              <>
                -{digits.slice(0, 4)}
                {digits.length > 4 ? `-${digits.slice(4)}` : ''}
              </>
            )}
          </span>
        </div>

        <div className="pkm-numpad">
          {NUMPAD_KEYS.map((row, ri) => (
            <div className="pkm-row" key={ri}>
              {row.map((key) => (
                <button
                  key={key}
                  className={`pkm-key ${key === 'clear' ? 'key-clear' : ''} ${key === 'backspace' ? 'key-backspace' : ''}`}
                  onClick={() => handleKeyPress(key)}
                  type="button"
                >
                  {key === 'backspace' ? '⌫' : key === 'clear' ? 'C' : key}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="pkm-actions">
          <button className="pkm-btn pkm-btn-cancel" onClick={onClose} type="button">
            취소
          </button>
          <button
            className="pkm-btn pkm-btn-confirm"
            onClick={handleConfirm}
            disabled={!canConfirm}
            type="button"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhoneKeypadModal;
